import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { NavController } from '@ionic/angular';

export interface DeepLinkData {
  url: string;
  path: string;
  params?: { [key: string]: string };
  queryParams?: { [key: string]: string };
}

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  
  constructor(
    private router: Router,
    private zone: NgZone,
    private navCtrl: NavController
  ) {}

  /**
   * Initialize deep link listeners
   */
  initializeDeepLinks() {
    
    App.addListener('appUrlOpen', (event: any) => {
      this.handleDeepLink(event);
    });

    // Also listen for app state changes to catch deep links when app is already running
    App.addListener('appStateChange', ({ isActive }) => {
    });

  }

  /**
   * Handle incoming deep link
   */
  handleDeepLink(event: any) {
    try {
      // Cerrar el navegador in-app (Custom Tab) si estaba abierto. Al volver del
      // onboarding de Stripe, la web de retorno dispara este deeplink; cerramos el
      // navegador para que no se quede abierto detrás de la app. No-op si no hay
      // ningún navegador abierto.
      Browser.close().catch(() => {});

      // Add delay for iOS to ensure app is fully loaded
      const platform = Capacitor.getPlatform();
      const delay = platform === 'ios' ? 500 : 0;
      
      setTimeout(() => {
        let deepLinkData;
        
        if (platform === 'ios') {
          deepLinkData = this.handleIOSDeepLink(event.url);
        } else {
          deepLinkData = this.parseDeepLink(event.url);
        }
        
        
        this.zone.run(() => {
          this.navigateToDeepLink(deepLinkData);
        });
      }, delay);
      
    } catch (error) {
    }
  }

  /**
   * Parse deep link URL and extract relevant data
   */
  private parseDeepLink(urlString: string): DeepLinkData {
    
    let path = '';
    let queryParams: { [key: string]: string } = {};
    
    try {
      // Handle custom scheme URLs like foundek://details/123
      if (urlString.startsWith('foundek://')) {
        const cleanUrl = urlString.replace('foundek://', '');
        
        const [pathPart, queryPart] = cleanUrl.split('?');
        
        // Add leading slash if not present
        path = pathPart.startsWith('/') ? pathPart : '/' + pathPart;
        
        if (queryPart) {
          const searchParams = new URLSearchParams(queryPart);
          searchParams.forEach((value, key) => {
            queryParams[key] = value;
          });
        }
      } else if (urlString.startsWith('/')) {
        // Handle cases where we just get the path part
        const [pathPart, queryPart] = urlString.split('?');
        path = pathPart;
        
        if (queryPart) {
          const searchParams = new URLSearchParams(queryPart);
          searchParams.forEach((value, key) => {
            queryParams[key] = value;
          });
        }
      } else {
        // Handle regular URLs
        const url = new URL(urlString);
        path = url.pathname;
        const searchParams = new URLSearchParams(url.search);
        
        searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }
    } catch (error) {
      // Fallback: treat the entire string as path
      path = urlString.startsWith('/') ? urlString : '/' + urlString;
    }
    
    
    return {
      url: urlString,
      path: path,
      params: {},
      queryParams: queryParams
    };
  }

  /**
   * Navigate to the appropriate route based on deep link data
   */
  private navigateToDeepLink(data: DeepLinkData) {
    
    // Handle different types of deep links
    if (data.path.startsWith('/details/')) {
      this.handleDetailsDeepLink(data);
    } else if (data.path === '/details') {
      this.handleDetailsQueryDeepLink(data);
    } else if (data.path.startsWith('/user-profile/')) {
      this.handleUserProfileDeepLink(data);
    } else if (data.path.startsWith('/messages/')) {
      this.handleMessagesDeepLink(data);
    } else if (data.path === '/notifications') {
      this.handleNotificationsDeepLink();
    } else if (data.path === '/rewards') {
      // Retorno del onboarding de Stripe al apartado de actividad.
      this.navCtrl.navigateRoot(['/rewards'], { animated: true });
    } else if (data.path === '/home' || data.path === '/') {
      this.handleHomeDeepLink();
    } else {
      this.handleDefaultDeepLink();
    }
  }

  /**
   * Handle details deep link with ID in path: /details/123
   */
  private handleDetailsDeepLink(data: DeepLinkData) {
    const pathSegments = data.path.split('/').filter(segment => segment.length > 0);
    const id = pathSegments[1]; // details/ID
    
    if (id) {
      this.navCtrl.navigateRoot(['/details', id], { animated: true });
    } else {
      this.handleDefaultDeepLink();
    }
  }

  /**
   * Handle details deep link with ID in query: /details?id=123
   */
  private handleDetailsQueryDeepLink(data: DeepLinkData) {
    const id = data.queryParams!['id']; // details?id=ID

    if (id) {
      this.navCtrl.navigateRoot(['/details', id], { animated: true });
    } else {
      this.handleDefaultDeepLink();
    }
  }

  /**
   * Handle user profile deep link: /user-profile/123
   */
  private handleUserProfileDeepLink(data: DeepLinkData) {
    const pathSegments = data.path.split('/').filter(segment => segment.length > 0);
    const id = pathSegments[1]; // user-profile/ID
    
    if (id) {
      this.navCtrl.navigateRoot(['/user-profile', id], { animated: true });
    } else {
      this.handleDefaultDeepLink();
    }
  }

  /**
   * Handle messages deep link: /messages/123
   */
  private handleMessagesDeepLink(data: DeepLinkData) {
    const pathSegments = data.path.split('/').filter(segment => segment.length > 0);
    const id = pathSegments[1]; // messages/ID
    
    if (id) {
      this.navCtrl.navigateRoot(['/app/messages', id], { animated: true });
    } else {
      this.handleDefaultDeepLink();
    }
  }

  /**
   * Handle notifications deep link: /notifications
   */
  private handleNotificationsDeepLink() {
    this.navCtrl.navigateRoot(['/app/notifications'], { animated: true });
  }

  /**
   * Handle home deep link: /home or /
   */
  private handleHomeDeepLink() {
    if (this.shouldIgnoreFallbackNavigation()) {
      return;
    }
    this.navCtrl.navigateRoot(['/app/home'], { animated: true });
  }

  /**
   * Handle default/fallback deep link
   */
  private handleDefaultDeepLink() {
    if (this.shouldIgnoreFallbackNavigation()) {
      return;
    }
    this.navCtrl.navigateRoot(['/app/home'], { animated: true });
  }

  /**
   * Avoids overriding an already-open chat/messages route with a fallback home navigation.
   */
  private shouldIgnoreFallbackNavigation(): boolean {
    const currentUrl = this.router.url || '';

    return currentUrl.startsWith('/app/messages')
      || currentUrl.startsWith('/app/chat')
      || currentUrl.includes('/messages/');
  }

  /**
   * Test deep link manually (for debugging)
   */
  testDeepLink(url: string) {
    this.handleDeepLink({ url: url });
  }

  /**
   * Get the initial URL if app was opened via deep link
   */
  async getInitialUrl(): Promise<string | null> {
    try {
      const result = await App.getLaunchUrl();
      return result?.url || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Force navigate to a specific route (for testing and debugging)
   */
  forceNavigateToRoute(path: string) {
    this.zone.run(() => {
      this.navCtrl.navigateRoot(path, { animated: true });
    });
  }

  /**
   * Handle iOS specific deep link processing
   */
  private handleIOSDeepLink(url: string) {
    
    // For iOS, we might need to handle the URL differently
    // Sometimes iOS passes the full URL, sometimes just the path
    let processedUrl = url;
    
    // Handle cases where iOS might pass different URL formats
    if (url.includes('foundek://')) {
      processedUrl = url;
    } else if (url.startsWith('/')) {
      processedUrl = 'foundek:/' + url;
    } else {
      processedUrl = 'foundek://' + url;
    }
    
    return this.parseDeepLink(processedUrl);
  }

}
